import React, {forwardRef} from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import {cn} from '../lib/utils';
import {Button} from './button';

export type AlertVariant = 'success' | 'error' | 'warning' | 'info';

/**
 * How a screen reader is told about the message: `assertive` interrupts
 * (`role="alert"`), `polite` waits for a pause (`role="status"`), and `off`
 * gives the box no live role at all.
 */
export type AlertLive = 'assertive' | 'polite' | 'off';

type AlertOwnProps = {
  variant: AlertVariant;
  /** A short line above the message. Like CardTitle it is not a heading: a consumer that needs one passes it. */
  title?: React.ReactNode;
  /** What the person can do about the message, such as a Retry button. Sits under it. */
  action?: React.ReactNode;
  /** Renders a Dismiss button that asks for this. The consumer removes the message. */
  onDismiss?: () => void;
  /**
   * Defaults to the variant's own: an error is `assertive`, everything else
   * `polite`, which is right for a message that follows something the person
   * did. Pass `off` for static content, and on a screen that already announces
   * through a polite region of its own, so nothing is read twice.
   */
  live?: AlertLive;
  /** Keeps the message at the top of the scrolling container it sits in. */
  sticky?: boolean;
  className?: string;
  children?: React.ReactNode;
};

/**
 * `role` is owned by the primitive and deliberately not part of the public
 * props: `live` is the one way to say how the message is announced.
 */
export type AlertProps = AlertOwnProps &
  Omit<React.HTMLAttributes<HTMLDivElement>, keyof AlertOwnProps | 'role'>;

export type FieldErrorProps = React.HTMLAttributes<HTMLParagraphElement>;

// Colour resolves through the Layer 2 status roles; geometry uses Tailwind's
// own scales, because Layer 2 exposes no geometry role. Text is always
// `--compass-on-surface`: the accent paints the border and the icon only, since
// an accent as text is what failed AA before these roles existed.
const alertStyles = [
  'my-4 flex items-start gap-3 rounded-md border p-4 text-(--compass-on-surface)',
  // A status tint is translucent, so it is painted as a flat image over the
  // opaque surface colour. The box then looks the same wherever it is placed,
  // and nothing shows through it when it sticks above scrolling content.
  'bg-(--compass-surface)',
  // Focus is moved here by a consumer's ref, never by Tab.
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)',
];

const variants: Record<AlertVariant, {box: string; icon: string; Icon: typeof CheckCircleIcon}> = {
  success: {
    box: 'border-(--compass-success-accent) bg-[image:linear-gradient(var(--compass-success-surface),var(--compass-success-surface))]',
    icon: 'text-(--compass-success-accent)',
    Icon: CheckCircleIcon,
  },
  error: {
    box: 'border-(--compass-error-accent) bg-[image:linear-gradient(var(--compass-error-surface),var(--compass-error-surface))]',
    icon: 'text-(--compass-error-accent)',
    Icon: ExclamationCircleIcon,
  },
  warning: {
    box: 'border-(--compass-warning-accent) bg-[image:linear-gradient(var(--compass-warning-surface),var(--compass-warning-surface))]',
    icon: 'text-(--compass-warning-accent)',
    Icon: ExclamationTriangleIcon,
  },
  info: {
    box: 'border-(--compass-info-accent) bg-[image:linear-gradient(var(--compass-info-surface),var(--compass-info-surface))]',
    icon: 'text-(--compass-info-accent)',
    Icon: InformationCircleIcon,
  },
};

const roles: Record<AlertLive, 'alert' | 'status' | undefined> = {
  assertive: 'alert',
  polite: 'status',
  off: undefined,
};

// What React renders nothing for: `null`, `undefined`, a boolean, an empty
// string, and any list of only those, which is what two `{condition && text}`
// children are while neither holds.
const isEmpty = (node: React.ReactNode) => React.Children.toArray(node).every((child) => child === '');

/**
 * A message with a status: success, error, warning or info.
 *
 * Render it whether or not there is anything to say. With no title and no
 * children it is an empty, visually hidden region, so the live role is in the
 * accessibility tree before the message arrives and the message is announced as
 * a change to a region already being watched. A box that is inserted carrying
 * its text is only reliably announced when it is an error.
 *
 * The primitive owns no timer and never removes itself. A consumer that clears
 * a message on a timer keeps a success on screen for at least five seconds and
 * never clears an error that way.
 *
 * `forwardRef` is called as the module loads, which a bundler has to assume
 * does something. The annotation says it does not, so an application that
 * renders neither primitive ships none of this module.
 */
export const Alert = /* @__PURE__ */ forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {variant, title, action, onDismiss, live, sticky = false, tabIndex = -1, className, children, ...props},
  ref
) {
  const {box, icon, Icon} = variants[variant];
  const role = roles[live ?? (variant === 'error' ? 'assertive' : 'polite')];

  if (isEmpty(title) && isEmpty(children)) {
    return (
      <div {...props} role={role} data-slot="alert" data-variant={variant} className="sr-only" ref={ref} />
    );
  }

  return (
    <div
      {...props}
      role={role}
      tabIndex={tabIndex}
      data-slot="alert"
      data-variant={variant}
      className={cn(alertStyles, box, sticky && 'sticky top-0 z-10', className)}
      ref={ref}>
      {/* One line tall, so the icon centres on the first line of text at any type size. */}
      <span className="flex h-lh shrink-0 items-center">
        <Icon className={cn('size-5 shrink-0', icon)} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1 wrap-break-word">
        {!isEmpty(title) && (
          <div data-slot="alert-title" className="font-semibold">
            {title}
          </div>
        )}
        {!isEmpty(children) && <div data-slot="alert-message">{children}</div>}
        {action && (
          <div data-slot="alert-action" className="mt-3">
            {action}
          </div>
        )}
      </div>
      {onDismiss && (
        // 44px each way, drawn out into the box's own padding: its icon sits
        // where the padding puts content, as the status icon does opposite it,
        // and a one-line message is not made taller by its close control.
        <Button
          variant="ghost"
          size="icon"
          className="-my-3 -mr-3 size-11 shrink-0"
          aria-label="Dismiss"
          onClick={onDismiss}>
          <XMarkIcon className="size-5" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
});

/**
 * The message under an invalid field: the error icon and on-surface text.
 * Renders nothing without a message, so a form passes its error straight in.
 *
 * Association is native, as it is for Label: the consumer gives this an `id`
 * and names it in the field's `aria-describedby`. The primitive adds no live
 * role of its own; a form that needs one passes `role="alert"`.
 */
export const FieldError = /* @__PURE__ */ forwardRef<HTMLParagraphElement, FieldErrorProps>(function FieldError(
  {className, children, ...props},
  ref
) {
  if (isEmpty(children)) return null;

  return (
    <p
      {...props}
      data-slot="field-error"
      className={cn('mt-2 flex items-start gap-1 text-sm font-medium text-(--compass-on-surface)', className)}
      ref={ref}>
      <span className="flex h-lh shrink-0 items-center">
        <ExclamationCircleIcon className="size-5 shrink-0 text-(--compass-error-accent)" aria-hidden="true" />
      </span>
      <span className="min-w-0 wrap-break-word">{children}</span>
    </p>
  );
});
